import React, { useEffect, useContext, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import AuthContext from '../../../middlewares/AuthContext'
import HomeNav from '../_components/HomeNav'
import Footer from '../_components/Footer'

// ── Types ─────────────────────────────────────────────────────────────────────
interface AuthCtx {
  token: string | null
  onLogin: (token: string) => void
  getHeaders: () => Promise<Record<string, string>>
}

// ── Constants ─────────────────────────────────────────────────────────────────
const BRAND        = '#6eb446'
const BRAND_SHADOW = '0 8px 24px rgba(110,180,70,0.3)'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

// ── Login ─────────────────────────────────────────────────────────────────────
const Login: React.FC = () => {
  const { t }                          = useTranslation()
  const { token, onLogin }             = useContext(AuthContext) as AuthCtx
  const [loading, setLoading]          = useState(false)
  const [showPass, setShowPass]        = useState(false)
  const navigate                       = useNavigate()
  const location                       = useLocation()
  const from: string                   = (location.state as any)?.from?.pathname ?? '/dashboard'

  useEffect(() => {
    document.body.classList.add('page-home')
    return () => document.body.classList.remove('page-home')
  }, [])

  if (token) return <Navigate to="/dashboard" replace />

  const formSchema = Yup.object().shape({
    email   : Yup.string().email().required(),
    password: Yup.string().required(),
  })

  const login = async (params: { email: string; password: string }) => {
    setLoading(true)
    try {
      const res = await axios.post(`${url}auth/login`, params, {
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
      onLogin(res.data.token)
      navigate(from, { replace: true })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    transition: 'border-color 0.15s',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 6,
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#374151',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f8faf6' }}>
      <HomeNav />

      <style>{`
        .lg-input:focus {
          border-color: ${BRAND} !important;
          box-shadow: 0 0 0 3px rgba(110,180,70,0.12) !important;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 1.5rem' }}>
        <div style={{
          width: '100%',
          maxWidth: 420,
          backgroundColor: '#fff',
          borderRadius: 24,
          boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9',
          overflow: 'hidden',
        }}>

          {/* Top accent */}
          <div style={{ height: 4, backgroundColor: BRAND }} />

          <div style={{ padding: '2.25rem 2rem 2rem' }}>
            {/* Logo + heading */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <img
                src="https://labeldictate.com/assets/img/Label-Dictate-Logo-V2021.png"
                width={56}
                alt="Label Dictate"
                style={{ marginBottom: '1rem' }}
              />
              <h1 style={{
                margin: 0,
                fontSize: '1.4rem',
                fontWeight: 800,
                color: '#1e293b',
                letterSpacing: '-0.01em',
              }}>
                {t('login.please_login')}
              </h1>
            </div>

            <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={formSchema}
              onSubmit={login}
            >
              <Form noValidate style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                {/* Email */}
                <div>
                  <label htmlFor="email" style={labelStyle}>{t('login.email')}</label>
                  <Field
                    id="email" name="email" type="email"
                    placeholder="name@example.com"
                    className="lg-input"
                    style={inputStyle}
                  />
                  <ErrorMessage name="email" render={msg => (
                    <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                  )} />
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" style={labelStyle}>{t('login.password')}</label>
                  <div style={{ position: 'relative' }}>
                    <Field
                      id="password" name="password"
                      type={showPass ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="lg-input"
                      style={{ ...inputStyle, paddingRight: 42 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(v => !v)}
                      style={{
                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#94a3b8', fontSize: '0.85rem', padding: 4,
                      }}
                    >
                      {showPass ? '🙈' : '👁'}
                    </button>
                  </div>
                  <ErrorMessage name="password" render={msg => (
                    <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                  )} />
                </div>

                {/* Forgot password */}
                <div style={{ textAlign: 'right', marginTop: -4 }}>
                  <Link to="/auth/recovery-password" style={{ fontSize: '0.8rem', color: BRAND, fontWeight: 600, textDecoration: 'none' }}>
                    {t('login.forgot_password')}
                  </Link>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '13px',
                    borderRadius: 9999,
                    border: 'none',
                    backgroundColor: loading ? '#a3d08a' : BRAND,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: BRAND_SHADOW,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    marginTop: 4,
                    transition: 'filter 0.15s',
                  }}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = 'brightness(1.08)' }}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)' }}
                >
                  {loading && (
                    <span style={{
                      width: 16, height: 16, borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff',
                      display: 'inline-block',
                      animation: 'spin 0.7s linear infinite',
                    }} />
                  )}
                  {t('header.login')}
                </button>

              </Form>
            </Formik>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '1.5rem 0' }}>
              <div style={{ flex: 1, height: 1, backgroundColor: '#f1f5f9' }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>o</span>
              <div style={{ flex: 1, height: 1, backgroundColor: '#f1f5f9' }} />
            </div>

            {/* Sign up link */}
            <Link
              to="/auth/signup"
              style={{
                display: 'block',
                width: '100%',
                padding: '12px',
                borderRadius: 9999,
                border: `1.5px solid ${BRAND}`,
                color: BRAND,
                fontWeight: 600,
                fontSize: '0.9rem',
                textDecoration: 'none',
                textAlign: 'center',
                boxSizing: 'border-box',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f4fbf0' }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
            >
              {t('login.create_account')}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Login
